// Countdown overlay component for the Buddha Game.
// Shows a 5-second countdown before the game starts.

import { useEffect, useState, useCallback } from 'react';

interface CountdownProps {
  /** Whether the countdown is active */
  isActive: boolean;
  /** Callback when countdown completes */
  onComplete: () => void;
}

export default function Countdown({ isActive, onComplete }: CountdownProps) {
  const [count, setCount] = useState(5);
  const [isFading, setIsFading] = useState(false);

  const handleComplete = useCallback(() => {
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    if (!isActive) {
      setCount(5);
      setIsFading(false);
      return;
    }

    // Start countdown from 5
    let currentCount = 5;
    setCount(currentCount);
    setIsFading(false);

    const intervalId = setInterval(() => {
      currentCount -= 1;

      if (currentCount > 0) {
        // Fade out current number, then update
        setIsFading(true);
        setTimeout(() => {
          setCount(currentCount);
          setIsFading(false);
        }, 200);
      } else {
        // Countdown complete - show 0 or "Breathe" briefly then complete
        setIsFading(true);
        setTimeout(() => {
          setCount(0);
          setIsFading(false);
        }, 200);

        // Complete after showing final count
        setTimeout(() => {
          clearInterval(intervalId);
          handleComplete();
        }, 800);
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isActive, handleComplete]);

  if (!isActive) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-[2px]"
      style={{
        // Safe area insets for mobile devices (avoid notch)
        paddingTop: 'max(env(safe-area-inset-top, 0px), 20px)',
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 20px)',
      }}
    >
      <div className="flex flex-col items-center justify-center gap-4 px-4">
        {/* Countdown number */}
        <div
          className={`text-[100px] font-bold text-white transition-all duration-300 sm:text-[140px] md:text-[160px] ${
            isFading ? 'scale-75 opacity-0' : 'scale-100 opacity-100'
          }`}
          style={{
            textShadow:
              '0 4px 20px rgba(0, 0, 0, 0.3), 0 0 40px rgba(255, 255, 255, 0.2)',
          }}
        >
          {count > 0 ? count : 'Breathe'}
        </div>

        {/* Optional subtitle */}
        {count > 0 && (
          <p
            className={`max-w-xs text-center text-sm text-white/80 transition-opacity duration-300 sm:max-w-md sm:text-base md:text-lg ${
              isFading ? 'opacity-0' : 'opacity-100'
            }`}
            style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
            }}
          >
            Get ready to breathe with the Buddha
          </p>
        )}
      </div>
    </div>
  );
}
